import { Alignment } from "@mcleod/core";
import { FilterTextboxPanel } from "./FilterTextboxPanel";
import { Panel } from "../../../..";
import { PanelProps } from "../../../panel/PanelProps";
import { RowCountLabel } from "./RowCountLabel";
import { SearchTextboxPanel } from "./SearchTextboxPanel";
import { Table } from "../../Table";

export type FilterAlignment = Alignment.BOTTOM | Alignment.RIGHT;

enum InnerIds {
    LABEL_ROW_COUNT = "labelRowCount",
    PANEL_FILTER = "panelFilter",
    PANEL_SEARCH = "panelSearch",
}

export class SearchFilterPanel extends Panel {
    private firstSearchRun = false;
    private _labelRowCount: RowCountLabel;
    private _panelFilter: FilterTextboxPanel;
    private _panelSearch: SearchTextboxPanel;
    private table: Table;

    constructor(table: Table, props?: Partial<PanelProps>) {
        super({ margin: 0, padding: 0, rowBreak: false, wrap: false, ...props });
        this.table = table;
        this.createComponents();
        this.add(this.panelSearch);
        this.add(this.panelFilter);
    }

    private createComponents() {
        this.createSearchTextbox();
        this.createFilterTextbox();
        this.createRowCountLabel();
    }

    private createSearchTextbox() {
        this.panelSearch = new SearchTextboxPanel(this.table,
            { id: this.createInnerComponentId(InnerIds.PANEL_SEARCH) });
        this.panelSearch.onFilterToggle = this.toggleFilterVisible;
    }

    private createFilterTextbox() {
        this.panelFilter = new FilterTextboxPanel(this.table,
            { id: this.createInnerComponentId(InnerIds.PANEL_FILTER) });
    }

    private createRowCountLabel() {
        this.labelRowCount = new RowCountLabel(this.table,
            { id: this.createInnerComponentId(InnerIds.LABEL_ROW_COUNT) });
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.labelRowCount, InnerIds.LABEL_ROW_COUNT);
        this.setInnerComponentId(this.panelFilter, InnerIds.PANEL_FILTER);
        this.setInnerComponentId(this.panelSearch, InnerIds.PANEL_SEARCH);
    }

    get panelSearch(): SearchTextboxPanel {
        return this._panelSearch;
    }

    private set panelSearch(value: SearchTextboxPanel) {
        this._panelSearch = value;
    }

    get panelFilter(): FilterTextboxPanel {
        return this._panelFilter;
    }

    private set panelFilter(value: FilterTextboxPanel) {
        this._panelFilter = value;
    }

    get labelRowCount(): RowCountLabel {
        return this._labelRowCount;
    }
    
    private set labelRowCount(value: RowCountLabel) {
        this._labelRowCount = value;
    }

    refreshTextboxSearchTooltip() {
        this.panelSearch.refreshTooltip();
    }

    refreshRowCountLabel() {
        this.labelRowCount.refresh();
    }

    syncFields(recreateFields: boolean) {
        if (this.panelFilter == null || this.panelSearch == null)
            return;
        const panelSearchVisible = this.checkPanelSearchVisibility();
        const panelFilterVisible = this.checkPanelFilterVisibility();
        this.panelSearch.syncFields(panelSearchVisible, recreateFields);
        this.panelFilter.syncFields(panelFilterVisible, recreateFields);
        this.syncLabelRowCount(panelSearchVisible || panelFilterVisible);
    }

    private syncLabelRowCount(makeVisible: boolean) {
        this.remove(this.labelRowCount);
        const panelSearchVisible = !this.panelSearch.isEmpty();
        if (panelSearchVisible) {
            //this.labelRowCount.rowBreak = this.table.filterAlignment === Alignment.BOTTOM;
            this.labelRowCount.rowBreak = false;
            this.insert(this.labelRowCount, 1);
        }
        else {
            this.labelRowCount.rowBreak = false;
            this.add(this.labelRowCount);
        }
        this.labelRowCount.visible = makeVisible;
    }

    private checkPanelSearchVisibility(): boolean {
        return this.table.allowDbSearch;
    }

    private checkPanelFilterVisibility(): boolean {
        // Only show the filter when
        // - It's allowed
        // - When the table contains records

        // Assume for now, where this is used, the filter is allowed
        // if (this.table.allowFilter !== true)
        //     return false;
        
        if (this.table.allowDbSearch === true) {
            // If in expanded mode, show the filter box when the user clicks the funnel image inside the search textbox.
            // If in collapsed mode, show the filter box when the user has run at least one search.
            // if (this.table.searchFilterMode === SearchFilterMode.EXPANDED) {
            //     return this.panelSearch.filterToggledOn;
            // }
            // else
                return this.firstSearchRun;
        }
        // The filter is allowed and the table is not empty, so display the filter.
        return true;
    }

    doAfterDbSearch() {
        this.panelSearch.updateAfterDbSearch(this.checkPanelFilterVisibility());
        this.firstSearchRun = true;
        this.syncFields(false);
        this.panelFilter.clearTextbox();
    }

    private toggleFilterVisible = () => {
        this.syncFields(false);
        this.panelSearch.syncFunnelColor(this.checkPanelFilterVisibility());
    }

    focusSearchFilter() {
        if (this.indexOf(this.panelSearch) >= 0)
            this.panelSearch.focus();
        else if (this.indexOf(this.panelFilter) >= 0)
            this.panelFilter.focus();
    }
}
